import { Icon } from '@iconify/react';
import { styled } from '@linaria/react';
import { Menu } from '@mantine/core';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ArtifactComponentProps } from '../artifact-registry';
import { getInitialWorkPeriodIndex } from '../flow-by-phase/flow-by-phase-artifact.helpers';
import { SingleObjectChart } from '../measure-comparison/measure-comparison-artifact';
import {
  buildScopedMeasuresOverTimeQuickLinks,
  buildWorkPeriodComparisonPayload,
  isMeasuresOverTimePayload,
  type MeasuresOverTimePayload,
} from './measures-over-time-artifact.helpers';

/* ── Component ─────────────────────────────────────────── */

/**
 * Renders the Flora chat artifact view for backend-provided Measures-over-Time
 * data: daily measure values across each day of a sprint. Sprint navigation
 * (prev/next + dropdown) lives here; the per-sprint chart body is delegated to
 * the shared `SingleObjectChart` so the line / bar / table toggle, dual axis,
 * legend, and tooltip are identical to the measure-comparison artifact.
 *
 * @param props - Standard Flora artifact component props. `initialState` (when
 *   provided by the export route) seeds `selectedIndex` via `workPeriodIndex`
 *   and the view via `chartType` (the latter is read by `SingleObjectChart`).
 * @returns Per-sprint measure chart/table with sprint navigation.
 */
export function MeasuresOverTimeArtifact({ data, config, onQuickLinksChange, initialState }: ArtifactComponentProps) {
  const payload = useMemo(() => {
    if (isMeasuresOverTimePayload(data)) return data as unknown as MeasuresOverTimePayload;
    return null;
  }, [data]);

  const workPeriods = useMemo(() => payload?.work_periods ?? [], [payload]);

  const [selectedIndex, setSelectedIndex] = useState(() => getInitialWorkPeriodIndex(initialState));

  // Clamp `selectedIndex` against the current `workPeriods.length`. The seed
  // from `initialState.workPeriodIndex` (or a stale value left over from a
  // previous payload) can otherwise drive `currentWP` to undefined.
  useEffect(() => {
    setSelectedIndex((index) => Math.min(index, Math.max(workPeriods.length - 1, 0)));
  }, [workPeriods.length]);

  const currentWP = workPeriods[selectedIndex];

  // Rebuild quick links scoped to the currently selected work period.
  useEffect(() => {
    if (!onQuickLinksChange) return;
    if (!currentWP) {
      onQuickLinksChange([]);
      return;
    }

    onQuickLinksChange(buildScopedMeasuresOverTimeQuickLinks(data, currentWP, config));
  }, [onQuickLinksChange, currentWP, config, data]);

  const comparisonPayload = useMemo(
    () => (currentWP ? buildWorkPeriodComparisonPayload(currentWP, payload?.measure_details) : null),
    [currentWP, payload],
  );

  const goPrev = useCallback(() => {
    setSelectedIndex((index) => Math.max(0, index - 1));
  }, []);

  const goNext = useCallback(() => {
    setSelectedIndex((index) => Math.min(workPeriods.length - 1, index + 1));
  }, [workPeriods.length]);

  if (!payload || workPeriods.length === 0) {
    return <EmptyState>No measures-over-time data available</EmptyState>;
  }

  if (!currentWP || !comparisonPayload) {
    return <EmptyState>Invalid work period selection</EmptyState>;
  }

  const hasManyWPs = workPeriods.length > 1;

  return (
    <ChartContainer>
      {/* ── Work-period navigation header ── */}
      <HeaderRow>
        <HeaderLeft>
          {hasManyWPs && (
            <NavBtn onClick={goPrev} disabled={selectedIndex === 0} aria-label="Previous work period">
              <Icon icon="material-symbols:chevron-left" width={16} height={16} />
            </NavBtn>
          )}

          {hasManyWPs ? (
            <Menu shadow="sm" radius="md" position="bottom-start" offset={4} withinPortal={false}>
              <Menu.Target>
                <WPDropdownBtn type="button" aria-label="Select work period">
                  <WPName>{currentWP.name || `Work Period ${selectedIndex + 1}`}</WPName>
                  <Icon icon="material-symbols:expand-more" width={14} height={14} />
                </WPDropdownBtn>
              </Menu.Target>
              <Menu.Dropdown>
                {workPeriods.map((wp, idx) => (
                  <Menu.Item
                    key={wp.id}
                    onClick={() => setSelectedIndex(idx)}
                    rightSection={
                      idx === selectedIndex ? <Icon icon="material-symbols:check" width={14} height={14} /> : undefined
                    }
                    style={{ fontSize: '13px' }}
                  >
                    {wp.name || `Work Period ${idx + 1}`}
                  </Menu.Item>
                ))}
              </Menu.Dropdown>
            </Menu>
          ) : (
            <WPName>{currentWP.name || 'Work Period'}</WPName>
          )}

          {hasManyWPs && (
            <NavBtn onClick={goNext} disabled={selectedIndex === workPeriods.length - 1} aria-label="Next work period">
              <Icon icon="material-symbols:chevron-right" width={16} height={16} />
            </NavBtn>
          )}

          {currentWP.start_date && currentWP.end_date && (
            <DateRange>
              {currentWP.start_date} – {currentWP.end_date}
            </DateRange>
          )}
        </HeaderLeft>
      </HeaderRow>

      {/* ── Per-sprint chart body (shared with measure comparison) ── */}
      <SingleObjectChart payload={comparisonPayload} config={config} initialState={initialState} />
    </ChartContainer>
  );
}

/* ── Styled components ─────────────────────────────────── */

const ChartContainer = styled.div`
  width: 100%;
`;

const EmptyState = styled.div`
  padding: 24px;
  text-align: center;
  color: var(--ink-lighter);
  font-size: 13px;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 0 4px 10px;
  flex-wrap: wrap;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const NavBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: var(--ink-base);
  cursor: pointer;
  flex-shrink: 0;

  &:hover:not(:disabled) {
    background: rgba(0, 0, 0, 0.06);
  }

  &:disabled {
    opacity: 0.3;
    cursor: default;
  }
`;

const WPDropdownBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 2px 6px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 6px;
  background: transparent;
  cursor: pointer;
  font-size: 13px;
  color: var(--ink-dark);
  font-weight: 500;

  &:hover {
    background: rgba(0, 0, 0, 0.04);
  }
`;

const WPName = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: var(--ink-dark);
`;

const DateRange = styled.span`
  font-size: 11px;
  color: var(--ink-lighter);
  margin-left: 6px;
`;
